import React from 'react'
import { ChevronDown } from 'lucide-react'

import { nutrimaticLabels } from '@/nutrimatic'
import CurrencyLogo from '../../../../components/CurrencyLogo'
import { cn } from '@/lib/utils'
import { useAppWeb3 } from '../../../../hooks'
import { useCurrencyBalance } from '../../../../state/wallet/hooks'
import type { Currency } from '../../../../sdk'
import { parseAmountInput } from '../../../../utils/amountInputFormat'
import { formatSwapAmountDisplay } from '../../../../utils/swapAmountDisplay'
import { MobileFluidAmount } from '../../../swap/nutrimatic/mobile/MobileFluidAmount'

export function NutrimaticMobileCurrencyLeg({
  label,
  value,
  currency,
  onOpenTokenPicker,
  onFocusAmount,
  onAmountChange,
  active,
  disabled,
  position,
  readOnly,
  useNativeInput = false,
}: {
  /** Etiqueta visible sobre la pata; omitir en flujos de liquidez donde el token basta como contexto. */
  label?: string
  value: string
  currency: Currency | null | undefined
  onOpenTokenPicker?: () => void
  onFocusAmount?: () => void
  onAmountChange?: (next: string) => void
  active?: boolean
  disabled?: boolean
  position: 'from' | 'to'
  readOnly?: boolean
  useNativeInput?: boolean
}) {
  const { account } = useAppWeb3()
  const balance = useCurrencyBalance(account ?? undefined, currency ?? undefined)
  const balanceLabel =
    currency && balance
      ? nutrimaticLabels.mobileSimple.balanceAmount(balance.toSignificant(4), currency.symbol ?? '')
      : null

  const amountAriaLabel = label
    ? `Editar monto ${label}`
    : currency
      ? `Editar monto ${currency.symbol}`
      : 'Editar monto'

  return (
    <section
      className={cn(
        'nm-mobile-simple__leg',
        position === 'from' ? 'nm-mobile-simple__leg--from' : 'nm-mobile-simple__leg--to',
        disabled && 'pointer-events-none opacity-60',
        readOnly && 'cursor-default',
        !label && 'nm-mobile-simple__leg--no-label',
      )}
      onClick={readOnly ? undefined : onFocusAmount}
      role="presentation"
      aria-label={label || undefined}
    >
      {label ? <div className="nm-mobile-simple__leg-label">{label}</div> : null}

      <div className="nm-mobile-simple__leg-main">
        {onOpenTokenPicker ? (
          <button
            type="button"
            className="nm-mobile-simple__token-chip"
            onClick={(event) => {
              event.stopPropagation()
              onOpenTokenPicker()
            }}
          >
            {currency ? <CurrencyLogo currency={currency} size="28px" /> : null}
            <span className="nm-mobile-simple__token-chip-symbol">{currency?.symbol ?? 'Elegir'}</span>
            <span className="nm-mobile-simple__token-chip-chevron" aria-hidden>
              <ChevronDown strokeWidth={2.5} />
            </span>
          </button>
        ) : currency ? (
          <div className="nm-mobile-simple__token-chip pointer-events-none">
            <CurrencyLogo currency={currency} size="28px" />
            <span className="nm-mobile-simple__token-chip-symbol">{currency.symbol}</span>
          </div>
        ) : null}

        {readOnly ? (
          <div className="nm-mobile-simple__amount-fluid nm-mobile-simple__amount-fluid--empty pointer-events-none">
            <span className="nm-mobile-simple__amount-fluid-content">
              <span className="nm-mobile-simple__amount-fluid-text">{value || '0'}</span>
            </span>
          </div>
        ) : useNativeInput ? (
          <input
            type="text"
            inputMode="decimal"
            className={cn(
              'nm-mobile-simple__amount-input',
              active && 'nm-mobile-simple__amount-input--active',
              !value && 'nm-mobile-simple__amount-input--empty',
            )}
            value={formatSwapAmountDisplay(value)}
            placeholder="0"
            onFocus={(event) => {
              event.stopPropagation()
              onFocusAmount?.()
            }}
            onChange={(event) => {
              event.stopPropagation()
              onAmountChange?.(parseAmountInput(event.target.value, value))
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label={amountAriaLabel}
          />
        ) : (
          <MobileFluidAmount
            value={value}
            active={active}
            empty={!value}
            onFocus={() => onFocusAmount?.()}
            aria-label={amountAriaLabel}
          />
        )}
      </div>

      {!readOnly ? (
        <div className="nm-mobile-simple__leg-balance-row">
          <span className="nm-mobile-simple__leg-balance-label">{nutrimaticLabels.mobileSimple.balance}</span>
          <span className="nm-mobile-simple__leg-balance-value">
            {account && balanceLabel ? balanceLabel : '—'}
          </span>
        </div>
      ) : null}
    </section>
  )
}

export function NutrimaticMobileTokenSelectLeg({
  label,
  currency,
  onOpenTokenPicker,
  position,
}: {
  label?: string
  currency: Currency | null | undefined
  onOpenTokenPicker: () => void
  position: 'from' | 'to'
}) {
  return (
    <section
      className={cn(
        'nm-mobile-simple__leg',
        position === 'from' ? 'nm-mobile-simple__leg--from' : 'nm-mobile-simple__leg--to',
        !label && 'nm-mobile-simple__leg--no-label',
      )}
    >
      {label ? <div className="nm-mobile-simple__leg-label">{label}</div> : null}
      <button type="button" className="nm-mobile-simple__token-chip w-full justify-start py-1" onClick={onOpenTokenPicker}>
        {currency ? <CurrencyLogo currency={currency} size="28px" /> : null}
        <span className="nm-mobile-simple__token-chip-symbol">{currency?.symbol ?? 'Elegir'}</span>
        <span className="nm-mobile-simple__token-chip-chevron ml-auto" aria-hidden>
          <ChevronDown strokeWidth={2.5} />
        </span>
      </button>
    </section>
  )
}
